/**
 * Staff client for the secure credential intake (`/api/credential-requests/*`
 * in sm4rt-api). SWA-header authenticated; issuance is a technician action and
 * the public surface can never originate one.
 *
 * ### The link comes back once and cannot be re-read
 *
 * A request's URL carries a signed capability, and sm4rt-api stores no record
 * of it — not even a hash — so nothing can reproduce it after the response that
 * returned it. Callers hold it in component state and nowhere else: not
 * `sessionStorage`, not a log entry, not an error message.
 *
 * `relink` is what "send it again" means here. It mints **another** capability
 * for the **same** `requestId`, so the new link shares the request's ticket
 * binding, its submission counter, the cap of ten and the revocation flag. It
 * does not create a second request, and it does not reveal the first link.
 *
 * ### SM4RT never sees a credential
 *
 * A submission carries a 1Password **deep link** and nothing else. The
 * technician follows it and authenticates to 1Password themselves, where the
 * viewing authorisation and the audit trail live. This app holds no 1Password
 * credential and could not read the item if it tried.
 *
 * Types are declared here rather than in `contracts/`: sm4rt-api does not
 * consume the TypeScript contracts for these DTOs, so a shared definition would
 * buy a submodule bump and prove nothing. Same call as `smsTestApi.ts`.
 */

const API_BASE_URL = '/api/credential-requests';

/** Lifecycle of a request row. Mirrors `credential_request_service`. */
export type CredentialRequestState = 'issued' | 'submitted' | 'revoked' | 'expired';

/** Delivery channel for the one-time link. */
export type CredentialDeliveryChannel = 'email' | 'sms';

export interface CredentialSubmissionDto {
  id: string;
  requestId: string;
  submissionSeq: string;
  idemKey: string;
  outcome: string;
  itemId: string;
  vaultId: string;
  completedAt: string;
  purgeEnqueuedAt: string;
  /** Opens the item in 1Password under the technician's own session. */
  deepLink: string;
}

export interface CredentialRequestDto {
  id: string;
  requestId: string;
  label: string;
  ticketId: string;
  customerRef: string;
  issuedBy: string;
  issuedAt: string;
  expiresAt: string;
  state: CredentialRequestState;
  revoked: boolean;
  revokedAt: string;
  revokedBy: string;
  /** Masked by the server, e.g. `s***@example.com` — never the full address. */
  deliveredTo: string;
  deliveredVia: string;
  deliveredAt: string;
  lastActivityAt: string;
  submissionCount: number;
  submissions: CredentialSubmissionDto[];
}

/**
 * The response to an issue or a relink. `link` is the only place the capability
 * appears and the only chance to read it.
 */
export interface CredentialLinkResult extends Omit<CredentialRequestDto, 'submissions' | 'submissionCount' | 'revoked' | 'revokedAt' | 'revokedBy' | 'lastActivityAt'> {
  link: string;
  fingerprint: string;
  /** Set when the send failed. The request is still valid and the link usable. */
  deliveryError: string;
}

export interface IssueCredentialRequest {
  label: string;
  ticketId?: string;
  customerRef?: string;
  deliverTo?: string;
  deliverVia?: CredentialDeliveryChannel;
}

export interface RelinkCredentialRequest {
  deliverTo?: string;
  deliverVia?: CredentialDeliveryChannel;
}

export interface RevokeCredentialResult {
  requestId: string;
  revoked: boolean;
  revokedAt: string;
}

const JSON_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

/** Surface the server's own message — it is written for an operator to read. */
async function failure(response: Response, fallback: string): Promise<Error> {
  const body = await response
    .json()
    .catch(() => ({ error: `${fallback}: ${response.status}` }));
  return new Error(body.error || `HTTP ${response.status}`);
}

async function readJson<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) throw await failure(response, fallback);
  return response.json();
}

/**
 * Every credential request raised on a RepairShopr ticket, newest first.
 *
 * There is deliberately no unfiltered list on the server, so `rsTicketId` is
 * required.
 */
export async function listCredentialRequests(
  rsTicketId: string,
  signal?: AbortSignal,
): Promise<CredentialRequestDto[]> {
  const response = await fetch(
    `${API_BASE_URL}?ticketId=${encodeURIComponent(rsTicketId)}`,
    { headers: { Accept: 'application/json' }, signal },
  );
  const data = await readJson<{ items?: CredentialRequestDto[] }>(
    response,
    'Failed to load credential requests',
  );
  return data.items ?? [];
}

/** One request and its submissions. */
export async function getCredentialRequest(
  requestId: string,
  signal?: AbortSignal,
): Promise<CredentialRequestDto> {
  const response = await fetch(`${API_BASE_URL}/${encodeURIComponent(requestId)}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  return readJson<CredentialRequestDto>(response, 'Failed to load the credential request');
}

/**
 * Raise a request and get the link.
 *
 * When `deliverTo` is given the server sends it and records the masked
 * destination; when it is omitted the delivery columns stay honestly empty and
 * the technician sends the link themselves.
 */
export async function issueCredentialRequest(
  body: IssueCredentialRequest,
): Promise<CredentialLinkResult> {
  const response = await fetch(API_BASE_URL, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return readJson<CredentialLinkResult>(response, 'Failed to create the credential request');
}

/**
 * A fresh link for an existing request — see the note at the top of this file.
 *
 * The label is not a parameter: the server takes it off the row, because it is
 * already on the customer's screen and on the title of every 1Password item the
 * request has produced.
 */
export async function relinkCredentialRequest(
  requestId: string,
  body: RelinkCredentialRequest = {},
): Promise<CredentialLinkResult> {
  const response = await fetch(`${API_BASE_URL}/${encodeURIComponent(requestId)}/relink`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  return readJson<CredentialLinkResult>(response, 'Failed to regenerate the link');
}

/**
 * Withdraw a request. This kills **every** link ever minted for it, because
 * revocation is recorded against the request id rather than against a token.
 */
export async function revokeCredentialRequest(
  requestId: string,
): Promise<RevokeCredentialResult> {
  const response = await fetch(`${API_BASE_URL}/${encodeURIComponent(requestId)}/revoke`, {
    method: 'POST',
    headers: JSON_HEADERS,
  });
  return readJson<RevokeCredentialResult>(response, 'Failed to revoke the credential request');
}

/** Whether a request can still take submissions. Drives the button's copy. */
export function isLiveCredentialRequest(request: CredentialRequestDto): boolean {
  return !request.revoked && request.state !== 'revoked' && request.state !== 'expired';
}
